/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { useIntl } from 'react-intl'

import { EuiButtonIcon, EuiConfirmModal, EuiToolTip } from '@elastic/eui'

import { useDeleteServerlessTrafficFilterMutation } from '@modules/network-security-lib/hooks/serverless'
import { addToast } from '@modules/cui/Toasts'
import { FormattedMessage } from '@modules/i18n'
import type { TrafficFilterInfo } from '@modules/network-user-api/v1/types'
import type { TrafficFilters } from '@modules/project-user-api/v1/types'

import TrafficFilterType from '../../TrafficFilterType'

type Props = {
  ruleset: TrafficFilterInfo
  associations: TrafficFilters
}

const ServerlessDeleteButton: React.FunctionComponent<Props> = ({ ruleset, associations }) => {
  const { formatMessage } = useIntl()

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const { mutate: deleteServerlessTrafficFilter, isLoading } =
    useDeleteServerlessTrafficFilterMutation()

  const onSuccess = () => {
    setIsConfirmModalOpen(false)
    addToast({
      id: `networkSecurity.list.delete.serverless.success`,
      color: `success`,
      title: (
        <FormattedMessage
          id='networkSecurity.list.delete.serverless.toast.success'
          defaultMessage='{type} {name} deleted'
          values={{
            type: <TrafficFilterType type={ruleset.type} />,
            name: <strong>{ruleset.name}</strong>,
          }}
        />
      ),
    })
  }

  const onError = () => {
    setIsConfirmModalOpen(false)
    addToast({
      id: `networkSecurity.list.delete.serverless.error`,
      color: `danger`,
      title: (
        <FormattedMessage
          id='networkSecurity.list.delete.serverless.toast.error'
          defaultMessage='Failed to delete {type} {name}'
          values={{
            type: <TrafficFilterType type={ruleset.type} />,
            name: <strong>{ruleset.name}</strong>,
          }}
        />
      ),
    })
  }

  const isDisabled = Boolean(associations.length)

  const button = (
    <EuiButtonIcon
      color='danger'
      disabled={isDisabled}
      iconType='trash'
      aria-label={formatMessage(
        { id: 'networkSecurity.list.delete.serverless', defaultMessage: 'Delete {name}' },
        { name: ruleset.name },
      )}
      onClick={() => setIsConfirmModalOpen(true)}
    />
  )

  if (isDisabled) {
    const content = (
      <FormattedMessage
        id='networkSecurity.list.delete.serverless.disabled'
        defaultMessage='Cannot delete a ruleset with active associations'
      />
    )

    return <EuiToolTip content={content}>{button}</EuiToolTip>
  }

  const confirmModal = isConfirmModalOpen && (
    <EuiConfirmModal
      buttonColor='danger'
      title={
        <FormattedMessage
          id='networkSecurity.list.delete.serverless.confirmTitle'
          defaultMessage='Are you sure?'
        />
      }
      cancelButtonText={
        <FormattedMessage
          id='networkSecurity.list.delete.serverless.cancel'
          defaultMessage='Cancel'
        />
      }
      confirmButtonText={
        <FormattedMessage
          id='networkSecurity.list.delete.serverless.confirm'
          defaultMessage='Delete'
        />
      }
      onCancel={() => setIsConfirmModalOpen(false)}
      onConfirm={() => deleteServerlessTrafficFilter({ id: ruleset.id }, { onSuccess, onError })}
      isLoading={isLoading}
    >
      <FormattedMessage
        id='networkSecurity.list.delete.serverless.confirmBody'
        defaultMessage='This will delete the ruleset {name}.'
        values={{ name: <strong>{ruleset.name}</strong> }}
      />
    </EuiConfirmModal>
  )

  return (
    <Fragment>
      {button}
      {confirmModal}
    </Fragment>
  )
}

export default ServerlessDeleteButton
